/**
 * dunning-dismiss-store — which dunning notices this tab has already dismissed.
 *
 * The dunning hooks are called from several components at once — AppShell asks
 * `useDunningAdvisory` whether an advisory is showing (to decide whether the
 * promotional banner may show), and the banner asks the same hook to render.
 * Dismissal used to live in each hook instance's own `useState`, so clicking ×
 * updated only the banner's copy: AppShell went on believing an advisory was up and
 * kept the promotional/trial banner hidden for the rest of the session. One store
 * means every caller sees the same answer.
 *
 * Keys identify the notice — for an advisory, the account, step and renewal date, so
 * dismissing one never silences the next; for the login popup, the account alone, since
 * it is now dismissed for the session rather than per rung. Memory-only: the advisory's
 * server-side ack is its durable record; the popup's sessionStorage entry only outlives
 * a reload within the same browser session.
 */
import { create } from 'zustand';

interface DunningDismissState {
  dismissed: ReadonlySet<string>;
  dismiss: (key: string) => void;
}

export const useDunningDismissStore = create<DunningDismissState>((set) => ({
  dismissed: new Set<string>(),
  dismiss: (key) =>
    set((s) => (s.dismissed.has(key) ? s : { dismissed: new Set(s.dismissed).add(key) })),
}));

/** The identity of one pre-emptive advisory: a renewal on a date, for an account. */
export function advisoryKey(
  accountId: string | undefined,
  step: string,
  advisoryAt: string | null | undefined,
): string {
  return `adv:${accountId ?? ''}:${step}:${advisoryAt ?? ''}`;
}

/**
 * The identity of the login popup, now that it is dismissed per SESSION rather than
 * per ladder rung.
 *
 * Keyed on the account alone: one dismissal covers the popup until the next sign-in,
 * and including the account still stops one user's dismissal silencing another's on a
 * shared browser. It used to include the episode's start and the rung id, which is why
 * the projection had to carry both — the popup was the only reader of either.
 */
export function popupKey(accountId: string | undefined): string {
  return `popup:${accountId ?? ''}`;
}
